import { LoadingOverlay } from '@mantine/core'
import { Button } from 'components/Button/Button'
import { translate } from 'i18n/i18n'
import { useEffect, useState } from 'react'
import { colors } from 'settings/colors'
import { useIsWideScreen } from 'utils/useIsWideScreen'
import { SettingsCreateNewButton } from '../../components/SettingsCreateNewButton/SettingsCreateNewButton'
import { SettingsFooterContainer } from '../../components/SettingsFooterContainer/SettingsFooterContainer'
import { ItemHeader } from '../../WideScreen/ItemHeader/ItemHeader'
import { useGroupsAndContacts } from '../ContactsAndGroups/ContactsAndGroupsContext'
import type { WeeklyGeneralReport, WeeklyTrapCatchesReport, WeeklyTrapIssuesReport } from './_types'
import { useReports } from './_utils/useReports'
import { ReportCard } from './components/ReportCard/ReportCard'
import type { TReportType } from './ReportForm/ReportType/ReportType'
import {
  WEEKLY_TRAP_CATCHES_REPORT_TYPE,
  WEEKLY_TRAP_ISSUES_REPORT_TYPE,
  WEEKLY_UPDATE_REPORT_TYPE,
} from './ReportForm/ReportType/ReportType'
import { ModalDrawerCreateOrEditReport } from './Reports/ModalDrawerCreateOrEditReport'

export type TModalDrawerForm = 'createReport' | 'editReport' | null

export const ReportsMain = () => {
  const [modalDrawerForm, setModalDrawerForm] = useState<TModalDrawerForm>(null)
  const isWideScreen = useIsWideScreen()
  const { initializeContactsAndGroups, loading: groupsAndContactsLoading } = useGroupsAndContacts()

  const [selectedReport, setSelectedReport] = useState<
    WeeklyGeneralReport | WeeklyTrapCatchesReport | WeeklyTrapIssuesReport | null
  >(null)

  const [isDuplicate, setIsDuplicate] = useState(false)

  useEffect(() => {
    initializeContactsAndGroups()
  }, [])

  const {
    reports,
    loading: reportsLoading,
    deleteReportHandler,
    updateReportHandler,
    createReportHandler,
  } = useReports()

  // State for report type
  const [reportType, setReportType] = useState<TReportType>('')
  const weeklyGeneralReports = reports.weeklyGeneral
  const weeklyTrapCatchReports = reports.weeklyTrapCatches
  const weeklyTrapIssuesReports = reports.weeklyTrapIssues

  return (
    <>
      <LoadingOverlay visible={reportsLoading || groupsAndContactsLoading} />
      <ItemHeader>{translate.phrases.banyanApp('Reports')}</ItemHeader>
      <div css={{ margin: '0px 20px', paddingBottom: 60 }}>
        {isWideScreen && (
          <div css={{ textAlign: 'right', marginBottom: 20 }}>
            <SettingsCreateNewButton
              label={translate.phrases.banyanApp('New Report')}
              onClick={() => {
                setModalDrawerForm('createReport')
              }}
            />
          </div>
        )}
        {weeklyGeneralReports.length === 0 &&
          weeklyTrapCatchReports.length === 0 &&
          weeklyTrapIssuesReports.length === 0 && (
            <div
              css={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                marginTop: 20,
                borderTop: `1px solid ${colors.grey200}`,
                padding: 20,
                marginBottom: 5,
              }}
            >
              {translate.phrases.banyanApp('No reports found.')}
              <Button
                variant="link"
                onClick={() => {
                  setModalDrawerForm('createReport')
                }}
              >
                {translate.phrases.banyanApp('Create one now')}
              </Button>
            </div>
          )}
        {weeklyTrapCatchReports.map((report) => (
          <ReportCard
            key={report.id} // Ensure report.id is unique and stable
            report={report}
            setSelectedReport={setSelectedReport}
            setModalDrawerForm={setModalDrawerForm}
            reportType={WEEKLY_TRAP_CATCHES_REPORT_TYPE}
            setReportType={setReportType}
            setIsDuplicate={setIsDuplicate}
            deleteReportHandler={deleteReportHandler}
          />
        ))}
        {weeklyTrapIssuesReports.map((report) => (
          <ReportCard
            key={report.id}
            report={report}
            setSelectedReport={setSelectedReport}
            setModalDrawerForm={setModalDrawerForm}
            reportType={WEEKLY_TRAP_ISSUES_REPORT_TYPE}
            setReportType={setReportType}
            setIsDuplicate={setIsDuplicate}
            deleteReportHandler={deleteReportHandler}
          />
        ))}
        {weeklyGeneralReports.map((report) => (
          <ReportCard
            key={report.id} // Ensure report.id is unique and stable
            report={report}
            setSelectedReport={setSelectedReport}
            setModalDrawerForm={setModalDrawerForm}
            reportType={WEEKLY_UPDATE_REPORT_TYPE}
            setReportType={setReportType}
            setIsDuplicate={setIsDuplicate}
            deleteReportHandler={deleteReportHandler}
          />
        ))}
      </div>
      {!isWideScreen && (
        <SettingsFooterContainer>
          <SettingsCreateNewButton
            label={translate.phrases.banyanApp('New Report')}
            onClick={() => {
              setModalDrawerForm('createReport')
            }}
            css={{ width: '100%' }}
          />
        </SettingsFooterContainer>
      )}
      <ModalDrawerCreateOrEditReport
        opened={modalDrawerForm !== null}
        onClose={() => {
          setModalDrawerForm(null)

          setSelectedReport(null)
        }}
        reportId={selectedReport ? selectedReport.id : null}
        reportType={reportType}
        setReportType={setReportType}
        selectedReport={selectedReport}
        isDuplicate={isDuplicate}
        setIsDuplicate={setIsDuplicate}
        updateReportHandler={updateReportHandler}
        createReportHandler={createReportHandler}
      />
    </>
  )
}
